import { CATALOG_FIELD_LIST, CatalogColumnSuggestion } from '../domain/ProductCatalogColumns';
import { async_command_dispatcher, query_dispatcher, sync_command_dispatcher } from '@/api/client';
import { Product, ProductPriceEntry } from '../domain/Product';
import { DocumentPartyKind, normalizeDocumentPartyKind } from '../domain/Albaran';
import {
  ProductCatalogDuplicateGroup,
  ProductCatalogDuplicateVariant,
  ProductCatalogImportResult,
  ProductBulkPreview,
  ProductBulkUpdateResult,
  ProductColumn,
  ProductColumnConfiguration,
  ProductFilter,
  ProductFilterConfiguration,
  ProductHistoryResult,
  ProductListFilter,
  ProductRepository,
  ProductRevision,
  ProductListResult,
} from '../domain/ProductRepository';

// Bookkeeping the catalog keeps but nobody reads in the change history.
const HIDDEN_HISTORY_FIELD_LIST = ['kind', 'is_deleted'];

// Mirrors what the history components render as "Sin valor".
const isEmptyHistoryValue = (value: any): boolean => (
  value === null || value === undefined || value === ''
);

interface ProductSearchResponse {
  status?: string;
  items?: Array<{ [key: string]: unknown }>;
  total_count?: number;
}

interface ProductCategoryListResponse {
  status?: string;
  items?: unknown[];
}

/** Typed column filters: only the ones with text travel, and never an empty map. */
function buildTextFilterMap(
  textFilterMap?: { [field: string]: string },
): { [field: string]: string } | null {
  if (!textFilterMap) return null;
  const typedFilterMap: { [field: string]: string } = {};
  Object.keys(textFilterMap).forEach((field) => {
    const typedText = String(textFilterMap[field] || '').trim();
    if (typedText) typedFilterMap[field] = typedText;
  });
  return Object.keys(typedFilterMap).length ? typedFilterMap : null;
}

function productSelectionParams(
  uuidList: string[],
  filter: ProductListFilter,
  allFiltered: boolean,
): { [key: string]: unknown } {
  const params: { [key: string]: unknown } = {
    uuid_list: uuidList,
    all_filtered: allFiltered,
    kind: normalizeDocumentPartyKind(filter.kind),
    search: filter.search || '',
    supplier_search: filter.supplierSearch || '',
    category: filter.category || '',
    filter_tax_percent: filter.taxPercent,
    price_state: filter.priceState || 'all',
    minimum_price: filter.minimumPrice,
    maximum_price: filter.maximumPrice,
  };
  const textFilterMap = buildTextFilterMap(filter.textFilterMap);
  if (textFilterMap) params.text_filter_map = textFilterMap;
  return params;
}

/** Product catalog adapter for the dedicated albaran_factura contracts. */
export class ApiProductRepository implements ProductRepository {
  async list(
    companyId: number,
    filter: ProductListFilter = {},
    limit: number | null = null,
    offset: number | null = null,
    sortBy: string | null = null,
    sortDesc = false,
  ): Promise<ProductListResult> {
    const params: any = {
      company_id: companyId,
      kind: normalizeDocumentPartyKind(filter.kind),
      search: filter.search || '',
      supplier_search: filter.supplierSearch || '',
      category: filter.category || '',
      price_state: filter.priceState || 'all',
      };
    if (filter.taxPercent !== null && filter.taxPercent !== undefined) {
      params.tax_percent = Number(filter.taxPercent);
    }
    if (filter.minimumPrice !== null && filter.minimumPrice !== undefined) {
      params.minimum_price = Number(filter.minimumPrice);
    }
    if (filter.maximumPrice !== null && filter.maximumPrice !== undefined) {
      params.maximum_price = Number(filter.maximumPrice);
    }
    const textFilterMap = buildTextFilterMap(filter.textFilterMap);
    if (textFilterMap) params.text_filter_map = textFilterMap;
    if (limit !== null) params.limit = limit;
    if (offset !== null) params.offset = offset;
    // Quien decide que columnas ordenan es el servidor, que las marca en la lista de columnas.
    if (sortBy) {
      params.sort_by = sortBy;
      params.sort_desc = sortDesc;
    }
    const res: any = await query_dispatcher.dispatch('albaran_factura.product.list', params);
    const items = ((res && res.items) || []).map((i: any) => this.toDomain(i));
    return { items, total_count: (res && res.total_count) || 0 };
  }

  async listCategories(companyId: number): Promise<string[]> {
    const response = await query_dispatcher.dispatch(
      'albaran_factura.product.categories',
      { company_id: companyId },
    ) as unknown as ProductCategoryListResponse;
    if (!response || response.status === 'error') throw new Error('Product category list failed');
    return (response.items || []).filter(category => typeof category === 'string') as string[];
  }

  async getColumnConfiguration(companyId: number): Promise<ProductColumnConfiguration> {
    const response: any = await query_dispatcher.dispatch(
      'albaran_factura.product.columns.get',
      { company_id: companyId },
    );
    if (!response || response.status === 'error') throw new Error('Product columns load failed');
    return {
      show: ApiProductRepository.parseColumnList(response.show),
      hide: ApiProductRepository.parseColumnList(response.hide),
    };
  }

  async saveColumnConfiguration(
    companyId: number,
    configuration: ProductColumnConfiguration,
  ): Promise<ProductColumnConfiguration> {
    const response: any = await sync_command_dispatcher.dispatch(
      'albaran_factura.product.columns.save',
      {
        company_id: companyId,
        show: configuration.show,
        hide: configuration.hide,
      },
    );
    const output = (response && response.output) || response;
    if (!output || output.status === 'error') throw new Error('Product columns save failed');
    return {
      show: ApiProductRepository.parseColumnList(output.show),
      hide: ApiProductRepository.parseColumnList(output.hide),
    };
  }

  async getFilterConfiguration(companyId: number): Promise<ProductFilterConfiguration> {
    const response: any = await query_dispatcher.dispatch(
      'albaran_factura.product.filters.get',
      { company_id: companyId },
    );
    if (!response || response.status === 'error') throw new Error('Product filters load failed');
    return {
      show: ApiProductRepository.parseFilterList(response.show),
      hide: ApiProductRepository.parseFilterList(response.hide),
    };
  }

  async saveFilterConfiguration(
    companyId: number,
    configuration: ProductFilterConfiguration,
  ): Promise<ProductFilterConfiguration> {
    const response: any = await sync_command_dispatcher.dispatch(
      'albaran_factura.product.filters.save',
      {
        company_id: companyId,
        show: configuration.show,
        hide: configuration.hide,
      },
    );
    const output = (response && response.output) || response;
    if (!output || output.status === 'error') throw new Error('Product filters save failed');
    return {
      show: ApiProductRepository.parseFilterList(output.show),
      hide: ApiProductRepository.parseFilterList(output.hide),
    };
  }

  /** Filter list boundary: the server sends the block each filter belongs to. */
  private static parseFilterList(responseFilterList: any): ProductFilter[] {
    if (!Array.isArray(responseFilterList)) return [];
    return responseFilterList.map((responseFilter: any) => ({
      text: responseFilter.text || '',
      value: responseFilter.value || '',
      block: responseFilter.block || '',
    }));
  }

  /** Column list boundary: the server marks a column with snake_case `filter_kind`. */
  private static parseColumnList(responseColumnList: any): ProductColumn[] {
    if (!Array.isArray(responseColumnList)) return [];
    return responseColumnList.map((responseColumn: any) => {
      const column: ProductColumn = {
        text: responseColumn.text || '',
        value: responseColumn.value || '',
      };
      if (responseColumn.align) column.align = responseColumn.align;
      if (responseColumn.filter_kind) column.filterKind = responseColumn.filter_kind;
      // `sortable` es el nombre que la tabla lee para pintar o no la flecha de la cabecera.
      if (responseColumn.is_sortable !== undefined) column.sortable = Boolean(responseColumn.is_sortable);
      if (responseColumn.sort_field) column.sortField = responseColumn.sort_field;
      return column;
    });
  }

  async getById(uuid: string, companyId: number, includeDeletedReference = false): Promise<Product | null> {
    const res: any = await query_dispatcher.dispatch('albaran_factura.product.get', {
      product_uuid: uuid,
      company_id: companyId,
      include_deleted_reference: includeDeletedReference,
    });
    if (!res || res.status === 'error') return null;
    return this.toDomain(res.item);
  }

  async searchForAlbaran(albaranUuid: string, companyId: number, search: string,
                         limit = 20): Promise<ProductListResult> {
    const response = await query_dispatcher.dispatch(
      'albaran_factura.product.search_for_albaran',
      { albaran_uuid: albaranUuid, company_id: companyId, search, limit },
    ) as unknown as ProductSearchResponse;
    if (!response || response.status === 'error') throw new Error('Product search failed');
    return {
      items: (response.items || []).map(item => this.toDomain(item)),
      total_count: response.total_count || 0,
    };
  }

  async searchForFactura(facturaUuid: string, companyId: number, search: string,
                         limit = 20): Promise<ProductListResult> {
    const response = await query_dispatcher.dispatch(
      'albaran_factura.product.search_for_factura',
      { factura_uuid: facturaUuid, company_id: companyId, search, limit },
    ) as unknown as ProductSearchResponse;
    if (!response || response.status === 'error') throw new Error('Product search failed');
    return {
      items: (response.items || []).map(item => this.toDomain(item)),
      total_count: response.total_count || 0,
    };
  }

  async save(product: Product, companyId: number): Promise<string> {
    const res: any = await sync_command_dispatcher.dispatch('albaran_factura.product.save', {
      uuid: product.uuid || '',
      payload: this.toWire(product),
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') throw new Error('Product save failed');
    const savedUuid = out.uuid || product.uuid;
    // Stamp the persisted identity back so a brand-new product reuses the same
    // instance instead of creating a second one.
    product.uuid = savedUuid;
    return savedUuid;
  }

  async history(productUuid: string, companyId: number,
                limit = 20, offset = 0, includeDeletedReference = false): Promise<ProductHistoryResult> {
    const response: any = await query_dispatcher.dispatch(
      'albaran_factura.product.history',
      {
        product_uuid: productUuid,
        company_id: companyId,
        limit,
        offset,
        include_deleted_reference: includeDeletedReference,
      },
    );
    if (!response || response.status === 'error') throw new Error('Product history failed');
    const revisionList = (response.items || []).map((revision: any) => ({
      uuid: revision.uuid || '',
      productUuid: revision.product_uuid || '',
      actorUserId: revision.actor_user_id || 0,
      changedAt: revision.changed_at || '',
      action: revision.action || '',
      source: revision.source || '',
      changeMap: ApiProductRepository.parseHistoryChangeMap(revision.changes),
      batchUuid: revision.batch_uuid || '',
    }));
    // A revision left with nothing worth reading is noise, so it is dropped.
    const visibleRevisionList = revisionList.filter(
      (revision: ProductRevision) => Object.keys(revision.changeMap).length > 0,
    );
    return {
      items: visibleRevisionList,
      total_count: response.total_count || 0,
      loadedCount: revisionList.length,
    };
  }

  private static parseHistoryChangeMap(
    changes: any,
  ): { [field: string]: { before: any; after: any } } {
    const changeMap: { [field: string]: { before: any; after: any } } = {};
    Object.keys(changes || {}).forEach((field) => {
      if (HIDDEN_HISTORY_FIELD_LIST.includes(field)) return;
      const change = changes[field];
      if (!change) return;
      // Both sides empty reads as "Sin valor → Sin valor", which says nothing.
      if (isEmptyHistoryValue(change.before) && isEmptyHistoryValue(change.after)) return;
      changeMap[field] = { before: change.before, after: change.after };
    });
    return changeMap;
  }

  async previewBulkUpdate(
    uuidList: string[],
    companyId: number,
    changes: { [field: string]: any },
    filter: ProductListFilter = {},
    allFiltered = false,
  ): Promise<ProductBulkPreview> {
    const output: any = await query_dispatcher.dispatch(
      'albaran_factura.product.bulk_preview',
      {
        ...productSelectionParams(uuidList, filter, allFiltered),
        company_id: companyId,
        changes,
      },
    );
    if (!output || output.status === 'error') throw new Error('Product bulk preview failed');
    return {
      token: output.token,
      selectedCount: output.selected_count || 0,
      modifiedCount: output.modified_count || 0,
      skippedCount: output.skipped_count || 0,
      sample: output.sample || [],
    };
  }

  async applyBulkUpdate(previewToken: string, companyId: number): Promise<ProductBulkUpdateResult> {
    const response: any = await sync_command_dispatcher.dispatch(
      'albaran_factura.product.bulk_update',
      { preview_token: previewToken, company_id: companyId },
    );
    const output = response && response.output ? response.output : response;
    if (!output || output.status !== 'success') throw new Error('Product bulk update failed');
    return { updatedCount: output.updated_count || 0, batchUuid: output.batch_uuid || '' };
  }

  async rollbackBulkUpdate(batchUuid: string, companyId: number): Promise<number> {
    const response: any = await sync_command_dispatcher.dispatch(
      'albaran_factura.product.bulk_rollback',
      { batch_uuid: batchUuid, company_id: companyId },
    );
    const output = response && response.output ? response.output : response;
    if (!output || output.status !== 'success') throw new Error('Product bulk rollback failed');
    return output.restored_count || 0;
  }

  async remove(
    uuids: string[],
    companyId: number,
    filter: ProductListFilter = {},
    allFiltered = false,
  ): Promise<number> {
    const response: any = await sync_command_dispatcher.dispatch(
      'albaran_factura.product.delete',
      {
        ...productSelectionParams(uuids, filter, allFiltered),
        company_id: companyId,
      },
    );
    const output = (response && response.output) || response;
    if (!output || output.status === 'error') throw new Error('Product delete failed');
    return output.deleted_count || 0;
  }

  private toDomain(raw: any): Product {
    const body: any = raw || {};
    return {
      uuid: body.uuid || '',
      kind: normalizeDocumentPartyKind(body.kind),
      cif_proveedor: body.cif_proveedor || '',
      nombre_proveedor: body.nombre_proveedor || '',
      nombre_producto: body.nombre_producto || '',
      codigo_interno: body.codigo_interno || '',
      codigo_externo: body.codigo_externo || '',
      unidad_medida: body.unidad_medida || '',
      precio_unitario: parseFloat(body.precio_unitario) || 0,
      tax_percent: parseFloat(body.tax_percent) || 0,
      ean: body.ean || '',
      category: body.category || '',
      units_per_case: parseInt(body.units_per_case) || 0,
      package_weight: parseFloat(body.package_weight) || 0,
      variation: body.variation || '',
      isPriceMissing: Boolean(body.is_price_missing),
      isDeleted: Boolean(body.is_deleted),
      priceList: ApiProductRepository.parsePriceList(body.price_list),
    };
  }

  private toWire(p: Product): any {
    return {
      kind: normalizeDocumentPartyKind(p.kind),
      cif_proveedor: p.cif_proveedor,
      nombre_proveedor: p.nombre_proveedor,
      nombre_producto: p.nombre_producto,
      codigo_interno: p.codigo_interno,
      codigo_externo: p.codigo_externo,
      unidad_medida: p.unidad_medida,
      precio_unitario: p.precio_unitario,
      tax_percent: p.tax_percent,
      ean: p.ean,
      category: p.category,
      units_per_case: p.units_per_case,
      package_weight: p.package_weight,
      variation: p.variation,
      is_price_missing: p.precio_unitario <= 0 && p.isPriceMissing,
      price_list: (p.priceList || []).map(entry => ({
        date: entry.date,
        price: entry.price,
      })),
    };
  }

  /** Price series boundary: a plain calendar day, never a timestamp. */
  private static parsePriceList(responsePriceList: any): ProductPriceEntry[] {
    if (!Array.isArray(responsePriceList)) return [];
    return responsePriceList.map((responseEntry: any) => ({
      date: (responseEntry && responseEntry.date) || '',
      price: parseFloat(responseEntry && responseEntry.price) || 0,
    }));
  }

  async suggestCatalogColumns(companyId: number, rowList: string[][]): Promise<CatalogColumnSuggestion> {
    const suggestion = await query_dispatcher.dispatch(
      'albaran_factura.product_catalog.columns',
      { company_id: companyId, row_list: rowList },
    ) as { status: string; header_row: number; column_map: { [field: string]: number } };
    if (!suggestion || suggestion.status !== 'success' || !Number.isInteger(suggestion.header_row)
        || suggestion.header_row < 1 || suggestion.header_row > rowList.length
        || !suggestion.column_map || typeof suggestion.column_map !== 'object') {
      throw new Error('Invalid catalog column suggestion');
    }
    const columnMap: { [field: string]: number } = {};
    CATALOG_FIELD_LIST.forEach(field => {
      const index = suggestion.column_map[field.value];
      if (Number.isInteger(index) && index >= 0 && index < rowList[suggestion.header_row - 1].length) {
        columnMap[field.value] = index;
      }
    });
    return { headerRow: suggestion.header_row, columnMap };
  }

  async importFile(companyId: number, supplierTaxId: string, supplierName: string,
                   filePath: string, originalFileName: string,
                   includeProductsWithoutPrice = false,
                   defaultTaxType: string | null = null,
                   defaultTaxPercent: number | null = null,
                   effectiveDate: string | null = null,
                   kind: DocumentPartyKind = 'in_invoice',
                   columnMap: { [field: string]: number } | null = null,
                   headerRow: number | null = null): Promise<ProductCatalogImportResult> {
    const commandParams: {
      company_id: number;
      supplier_tax_id: string;
      supplier_name: string;
      file_path: string;
      original_file_name: string;
      include_products_without_price?: boolean;
      default_tax_type?: string;
      default_tax_percent?: number;
      valid_from?: string;
      column_map?: { [field: string]: number };
      header_row?: number;
      kind?: DocumentPartyKind;
    } = {
      company_id: companyId,
      supplier_tax_id: supplierTaxId,
      supplier_name: supplierName,
      file_path: filePath,
      original_file_name: originalFileName,
    };
    if (columnMap && headerRow) {
      commandParams.column_map = columnMap;
      commandParams.header_row = headerRow;
    }
    if (kind === 'out_invoice') commandParams.kind = kind;
    if (includeProductsWithoutPrice) {
      commandParams.include_products_without_price = true;
    }
    if (defaultTaxType !== null && defaultTaxPercent !== null) {
      commandParams.default_tax_type = defaultTaxType;
      commandParams.default_tax_percent = defaultTaxPercent;
    }
    if (effectiveDate) {
      commandParams.valid_from = effectiveDate;
    }
    const response: any = await async_command_dispatcher.dispatch_with_result(
      'albaran_factura.product_catalog.import',
      commandParams,
      2000,
    );
    return ApiProductRepository.parseCatalogImportResult(response);
  }

  async resolveCatalogDuplicates(
    duplicateToken: string,
    selectionMap: { [supplierCode: string]: number },
    companyId: number,
  ): Promise<ProductCatalogImportResult> {
    const response: any = await async_command_dispatcher.dispatch_with_result(
      'albaran_factura.product_catalog.resolve_duplicates',
      {
        preview_token: duplicateToken,
        selections: selectionMap,
        company_id: companyId,
      },
      2000,
    );
    return ApiProductRepository.parseCatalogImportResult(response);
  }

  private static parseCatalogImportResult(response: any): ProductCatalogImportResult {
    const output: any = (response && response.output) || response || {};
    const warningList = output.warnings || [];
    const missingPriceCount = typeof output.missing_price_count === 'number'
      ? output.missing_price_count
      : warningList.filter((warning: any) => warning.code === 'imported_without_price').length;
    return {
      status: output.status || 'error',
      createdCount: output.created_count || 0,
      updatedCount: output.updated_count || 0,
      missingPriceCount,
      warningCount: output.warning_count || 0,
      errorList: output.errors || [],
      warningList,
      duplicateToken: output.token || '',
      duplicateGroupList: ApiProductRepository.parseDuplicateGroupList(output.duplicate_groups),
    };
  }

  private static parseDuplicateGroupList(groupList: any): ProductCatalogDuplicateGroup[] {
    if (!groupList) return [];
    return groupList.map((group: any): ProductCatalogDuplicateGroup => ({
      supplierCode: group.supplier_code || '',
      variantList: (group.variants || []).map(
        (variant: any): ProductCatalogDuplicateVariant => {
          const product = variant.product || {};
          return {
            index: variant.index || 0,
            sourceRowList: variant.source_rows || [],
            supplierCode: product.supplier_code || '',
            ean: product.ean || '',
            description: product.description || '',
            category: product.category || '',
            taxPercent: product.tax_percent || 0,
            unitPrice: product.unit_price || 0,
            unitsPerCase: product.units_per_case || 0,
            packageWeight: product.package_weight || 0,
          };
        },
      ),
    }));
  }
}
